// Adjust-stars modal — invoked when an adult taps a chip in the
// Family Stars Bar. Lets the adult grant or deduct stars with an
// optional reason. Backed by households.adjustStars which writes
// to the star_adjustments audit table (households_store).

import { useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type FamilyStarsMember } from "../../lib/rpc";

interface Props {
  member: FamilyStarsMember;
  onClose: () => void;
  onAdjusted: (newTotal: number) => void;
  notify: (msg: string) => void;
}

const QUICK_DELTAS = [1, 5, -1, -5];

export function AdjustStarsModal({ member, onClose, onAdjusted, notify }: Props) {
  const [delta, setDelta] = useState<number>(1);
  const [reason, setReason] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  const submit = async () => {
    if (saving) return;
    if (!Number.isFinite(delta) || delta === 0) return;
    setSaving(true);
    try {
      const total = await api.householdsAdjustStars(
        member.userId,
        Math.trunc(delta),
        reason.trim() || undefined,
      );
      notify(`${delta > 0 ? "+" : ""}${delta}⭐ ${member.displayName} (${total} total)`);
      onAdjusted(total);
      onClose();
    } catch (err: any) {
      notify(err?.message || "couldn't adjust stars");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-sheet adjust-stars-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Adjust stars"
        data-testid="adjust-stars-modal"
      >
        <header className="adjust-stars-head">
          <span
            className="adjust-stars-avatar"
            style={{ background: member.avatarColor || "var(--accent)" }}
            aria-hidden
          >
            {member.avatarEmoji || member.displayName.charAt(0).toUpperCase()}
          </span>
          <div>
            <div className="adjust-stars-name">{member.displayName}</div>
            <div className="adjust-stars-total">{member.totalStars} ⭐ currently</div>
          </div>
          <button className="adjust-stars-close" type="button" onClick={onClose} aria-label="Close">
            <Icon name="x" size={16} />
          </button>
        </header>

        <div className="adjust-stars-quick">
          {QUICK_DELTAS.map((d) => (
            <button
              key={d}
              type="button"
              className="adjust-stars-quick-btn"
              data-active={delta === d}
              onClick={() => setDelta(d)}
              data-testid={`adjust-stars-quick-${d}`}
            >
              {d > 0 ? `+${d}` : d}
            </button>
          ))}
        </div>

        <label className="adjust-stars-custom">
          <span className="adjust-stars-label">Custom</span>
          <input
            type="number"
            value={String(delta)}
            onInput={(e) => setDelta(Number((e.target as HTMLInputElement).value))}
            min={-1000}
            max={1000}
            data-testid="adjust-stars-custom-input"
          />
        </label>

        <label className="adjust-stars-reason">
          <span className="adjust-stars-label">Reason (optional)</span>
          <input
            type="text"
            value={reason}
            onInput={(e) => setReason((e.target as HTMLInputElement).value)}
            placeholder="Extra help with dinner"
            maxLength={200}
            data-testid="adjust-stars-reason-input"
          />
        </label>

        <footer className="adjust-stars-foot">
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            type="button"
            onClick={submit}
            disabled={saving || delta === 0}
            data-testid="adjust-stars-submit"
          >
            {saving ? "Saving…" : (delta >= 0 ? `Grant ${delta} ⭐` : `Deduct ${Math.abs(delta)} ⭐`)}
          </button>
        </footer>
      </div>
    </div>
  );
}
